/**
 * The redirector's decisions, as pure functions.
 *
 * Every published URL — `<host>/p/<slug>`, and on the science site its readable
 * spelling `<host>/<page…>/<source>` — is answered at the edge by a
 * Cloudflare Pages Function on the property that owns it. This module is what
 * that function decides with, so the same rules hold on both properties and can
 * be tested without a worker.
 *
 * See docs/FeatureDesigns/acquisition/published-links-target.md §6.
 */

/** What the edge needs of a link. The resolve endpoint returns exactly this. */
export interface EdgeLink {
    slug: string;
    /** Live when the backend answered. A copy the backend PUSHES to the edge is
     *  read long after that, so it also carries what `live` was worked out
     *  from — see {@link isEdgeLinkLive}. */
    live: boolean;
    status?: 'active' | 'retired';
    startsAt?: string;
    expiresAt?: string;
    destination:
        | { kind: 'chapter-practice' | 'question-set'; questionSetId: string }
        | { kind: 'simulation'; simulationId: string }
        | { kind: 'route'; path: string }
        | { kind: 'site'; url: string };
    /** For the preview card a platform draws, and for a landing page. */
    preview?: { title?: string; description?: string; imageUrl?: string };
}

/**
 * Who is asking.
 *
 * A previewer is a platform fetching the URL to draw its card — it must get
 * tags, not a redirect, and must never be counted as an arrival. Other
 * automation is redirected like anyone else but goes uncounted: a crawler
 * following a link in a description is not a person who came.
 */
export type Caller = 'previewer' | 'automation' | 'person';

const PREVIEWERS = /(facebookexternalhit|facebookcatalog|WhatsApp|Twitterbot|TelegramBot|Slackbot|LinkedInBot|Discordbot|SkypeUriPreview|Iframely|redditbot|Google-InspectionTool|Googlebot|bingbot)/i;
const AUTOMATION = /(bot|crawler|spider|crawling|headless|lighthouse|slurp|monitor|curl|wget|python-requests|axios)/i;

export function classifyCaller(userAgent: string | null | undefined): Caller {
    const ua = userAgent ?? '';
    if (PREVIEWERS.test(ua)) return 'previewer';
    if (AUTOMATION.test(ua)) return 'automation';
    return 'person';
}

/**
 * An id for this arrival: the UTC date, then randomness.
 *
 * The date prefix is the `site-events` partition key, so the row this names can
 * be read back as a point read rather than a scan across a term of partitions.
 */
export function newArrivalId(now: Date, random: () => number = Math.random): string {
    const day = now.toISOString().slice(0, 10).replace(/-/g, '');
    const tail = Math.floor(random() * 0xffffffff).toString(36).padStart(6, '0');
    return `${day}-${tail}`;
}

/** The query keys a published URL may carry through — the allowlist that the
 *  "never the query string" rule names as its one exception (target §7.1). */
export const CARRIED_PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id',
    'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid',
] as const;

export interface ArrivalFacts {
    channel?: string;
    utm?: Record<string, string>;
    click?: { param: string; id: string };
}

/** What the URL says about where this arrival came from. */
export function arrivalFacts(params: URLSearchParams): ArrivalFacts {
    const utm: Record<string, string> = {};
    let click: { param: string; id: string } | undefined;
    for (const key of CARRIED_PARAMS) {
        const value = params.get(key);
        if (!value) continue;
        if (key.startsWith('utm_')) utm[key.slice(4)] = value.slice(0, 150);
        else if (!click) click = { param: key, id: value.slice(0, 500) };
    }
    const channel = params.get('c')?.trim().toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 24);
    return {
        ...(channel ? { channel } : {}),
        ...(Object.keys(utm).length ? { utm } : {}),
        ...(click ? { click } : {}),
    };
}

/**
 * Where to send the follower.
 *
 * The destination is built from the link, never from the request: that is what
 * keeps this a redirector and not an open redirect. The arrival id, the slug
 * and the channel ride along so the page can join itself to the arrival the
 * edge recorded, and so do the ad's own tags (`CARRIED_PARAMS`): the page
 * reports an ad click from its own URL, and would otherwise see none.
 */
export function redirectTarget(
    link: EdgeLink,
    opts: {
        appOrigin: string; arrivalId: string; channel?: string;
        utm?: Record<string, string>; click?: { param: string; id: string };
    },
): string {
    const d = link.destination;
    const path =
        d.kind === 'chapter-practice' ? `/practice/${link.slug}`
        : d.kind === 'question-set' ? `/practice/${link.slug}`
        : d.kind === 'simulation' ? `/simulations/${d.simulationId}`
        : d.kind === 'route' ? d.path
        : null;

    const url = new URL(path ?? (d as { url: string }).url, opts.appOrigin);
    url.searchParams.set('a', opts.arrivalId);
    url.searchParams.set('link', link.slug);
    if (opts.channel) url.searchParams.set('c', opts.channel);
    // The ad's own tags ride through to the page — `CARRIED_PARAMS`, nothing
    // else. The page reports an ad click (Meta's `reportAdClick`) from its own
    // URL: the click id, and the campaign and ad from the utm tags. Dropped here,
    // a paid follow of a published link reached nothing downstream — on prod,
    // 2026-09-27, 1 click recorded against ~150 follows per campaign, and no
    // conversion could be reported to Meta for the rest.
    for (const [k, v] of Object.entries(opts.utm ?? {})) url.searchParams.set(`utm_${k}`, v);
    if (opts.click) url.searchParams.set(opts.click.param, opts.click.id);
    return url.toString();
}

/* ── Page links: `<host>/<page…>/<source>` ─────────────────────────── */

/**
 * The readable spelling of a published link on the science site:
 * `zitmus.buzzbrain.ai/optics-bench/vkdadu` is the link `/p/optics-bench-vkdadu`.
 * One document, one slug, one count — the path is a second spelling, so the
 * slug pattern, the cache and the admin page need nothing new.
 *
 * Any page works, at any depth (`/simulations/projectile-motion/vkdadu` is
 * `simulations-projectile-motion-vkdadu`): the science site answers every path
 * that would otherwise 404 with one catch-all function
 * (`sciencexp-app/functions/[[path]].ts`), so there is no list of pages.
 */

/** The hosts that answer the path form — the science site's. */
export const PAGE_LINK_HOSTS = ['zitmus.com', 'www.zitmus.com', 'dev.zitmus.com', 'zitmus.buzzbrain.ai'] as const;

/** The same pattern as a stored slug (`PUBLISHED_LINK_SLUG_RE` on the backend). */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;
const SEGMENT_RE = /^[a-z0-9][a-z0-9-]*$/;

const trimSlashes = (path: string) => path.replace(/^\/+|\/+$/g, '');

/** The slug `<page…>/<source>` is stored under: the page's segments joined by
 *  hyphens, then the source. */
export const pageLinkSlug = (page: string, source: string): string =>
    `${trimSlashes(page).replace(/\//g, '-')}-${source}`;

/**
 * Read a request path as `<page…>/<source>`, or null when it cannot be one: at
 * least two segments, each plain lowercase letters, digits and hyphens (so an
 * asset like `/x/app.js` or a probe like `/wp-admin/setup.php` never reaches
 * the backend), and a slug that fits the stored pattern.
 */
export function parsePagePath(pathname: string): { page: string; source: string; slug: string } | null {
    const segments = trimSlashes(pathname).split('/');
    if (segments.length < 2 || !segments.every(s => SEGMENT_RE.test(s))) return null;
    const source = segments.pop()!;
    const page = segments.join('/');
    const slug = pageLinkSlug(page, source);
    return SLUG_RE.test(slug) ? { page, source, slug } : null;
}

/**
 * The readable form a link has, if it has one: a `site` link to a science-site
 * page whose slug is `<that page, hyphenated>-<source>`. A slug that merely
 * starts with a page name but points elsewhere has none — the path form must
 * land on the page it names.
 */
export function pageLinkOf(link: Pick<EdgeLink, 'slug' | 'destination'>): { page: string; source: string } | null {
    if (link.destination.kind !== 'site') return null;
    let url: URL;
    try { url = new URL(link.destination.url); } catch { return null; }
    if (!(PAGE_LINK_HOSTS as readonly string[]).includes(url.host)) return null;
    const page = trimSlashes(url.pathname);
    const prefix = pageLinkSlug(page, '');
    if (!page || !link.slug.startsWith(prefix)) return null;
    const source = link.slug.slice(prefix.length);
    return source ? { page, source } : null;
}

/** How a follow that went nowhere is logged: a slug the backend does not know,
 *  or a link that is retired or outside its window. Never counted as an open. */
export type DeadLinkOutcome = 'unknown' | 'ended';

const escapeHtml = (s: string) =>
    s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

/**
 * The card a platform draws for a pasted URL.
 *
 * Served only to previewers. A single-page app cannot give them this: its one
 * `index.html` carries the same tags for every URL, so today every link
 * previews as the same generic page.
 */
export function previewHtml(link: EdgeLink, url: string): string {
    const title = escapeHtml(link.preview?.title ?? 'BuzzBrain');
    const description = escapeHtml(link.preview?.description ?? '');
    const image = link.preview?.imageUrl ? escapeHtml(link.preview.imageUrl) : '';
    return `<!doctype html>
<html lang="en"><head>
<meta charset="utf-8">
<title>${title}</title>
<meta property="og:title" content="${title}">
${description ? `<meta property="og:description" content="${description}">` : ''}
${image ? `<meta property="og:image" content="${image}">` : ''}
<meta property="og:url" content="${escapeHtml(url)}">
<meta property="og:type" content="website">
<meta name="twitter:card" content="${image ? 'summary_large_image' : 'summary'}">
</head><body><p>${title}</p></body></html>`;
}


/* ── The handler itself ──────────────────────────────────────────────── */

/**
 * The minimum of a KV namespace this needs, so the shared package does not
 * depend on Cloudflare's types: both properties pass their real binding.
 */
export interface LinkCache {
    get(key: string, type: 'json'): Promise<unknown>;
    put(key: string, value: string): Promise<unknown>;
}

export interface RedirectEnv {
    /** The backend this property talks to. */
    apiBase: string;
    /** A cached copy of the links, so a follow does not wait on the backend.
     *  Optional: without it every follow asks the backend directly. */
    cache?: LinkCache;
}

/** How long a cached link is served before it is refreshed behind the
 *  follower. A repoint or retirement takes effect within this and one visit. */
export const STALE_AFTER_MS = 10 * 60 * 1000;

/**
 * A link as the edge keeps it. `pushed` marks a copy the backend wrote when the
 * link was saved: it is the table's own copy, kept current on every save, so it
 * is never refreshed behind a follower. A copy without it was fetched on a miss
 * and goes stale after {@link STALE_AFTER_MS}, as before.
 */
export interface CachedLink { link: EdgeLink; fetchedAt: number; pushed?: boolean }

/**
 * Whether a link is live NOW — worked out at the edge from its status and
 * window when the copy carries them, rather than trusting a `live` that was
 * true when the copy was made. A pushed copy can be days old: a link whose
 * window opens next week would otherwise never open, and one that expired
 * would never close. Mirrors `isLive` on the backend, end instant inclusive.
 */
export function isEdgeLinkLive(link: EdgeLink, now: Date): boolean {
    if (link.status === undefined) return link.live;
    if (link.status !== 'active') return false;
    const at = now.toISOString();
    if (link.startsAt && at < link.startsAt) return false;
    if (link.expiresAt && at > link.expiresAt) return false;
    return true;
}

/**
 * The backend's answer, or which way it failed to answer. The two failures are
 * handled differently: a link the backend does not know is logged and sent
 * home, but a backend that cannot be reached must not turn every published URL
 * into a dead one — the app's own /p/ route serves those instead.
 */
async function fetchLink(apiBase: string, slug: string): Promise<EdgeLink | 'missing' | 'unreachable'> {
    let res: Response;
    try {
        res = await fetch(`${apiBase}/public/link/${encodeURIComponent(slug)}`, {
            headers: { accept: 'application/json' },
            signal: AbortSignal.timeout(8_000),
        });
    } catch {
        return 'unreachable';
    }
    if (res.status === 404) return 'missing';
    if (!res.ok) return 'unreachable';
    // The API wraps a payload in `data`; accept a bare one too, so a direct
    // fetch in a test or from another caller reads the same.
    const body = await res.json() as { data?: EdgeLink } & Partial<EdgeLink>;
    const link = body.data ?? (body.slug ? body as EdgeLink : null);
    return link ?? 'missing';
}

/**
 * Answer one `<host>/p/<slug>`.
 *
 * Nothing waits on a write: the arrival is recorded through `waitUntil`, after
 * the response has gone out, so a slow or failing backend costs a row and never
 * a redirect.
 */
export async function handleLinkRedirect(opts: {
    request: Request;
    env: RedirectEnv;
    slug: string;
    waitUntil: (p: Promise<unknown>) => void;
    /** What to do when the backend cannot be reached and nothing is cached:
     *  the app's own route for this URL. Pages passes `context.next`. */
    fallback: () => Promise<Response>;
    /**
     * Set when answering `<page…>/<source>` rather than `/p/<slug>` — the page's
     * path, e.g. `optics-bench`. The link must then be that page's
     * ({@link pageLinkOf}), and it lands on the page on the host that was
     * followed. Anything that is not a live link for the page — unknown, ended,
     * or pointing elsewhere — lands on the page itself: the URL already says
     * where the person meant to go, and the caller has checked the page exists.
     */
    page?: string;
    now?: Date;
}): Promise<Response> {
    const { request, env, waitUntil, page } = opts;
    const slug = opts.slug.toLowerCase();
    const url = new URL(request.url);
    const appOrigin = url.origin;
    const pageUrl = page ? new URL(`/${page}/`, appOrigin).toString() : null;

    const now = opts.now ?? new Date();
    const caller = classifyCaller(request.headers.get('user-agent'));
    const facts = arrivalFacts(url.searchParams);
    const record = (extra: Record<string, unknown>) => {
        let referrerHost: string | undefined;
        try { referrerHost = new URL(request.headers.get('referer') ?? '').host || undefined; } catch { /* none */ }
        waitUntil(fetch(`${env.apiBase}/public/link-open`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
                slug,
                ...(referrerHost ? { referrerHost } : {}),
                ...(((request as { cf?: { country?: string } }).cf?.country)
                    ? { country: (request as { cf?: { country?: string } }).cf!.country } : {}),
                // What the follow arrived with, as it arrived — kept whole so it
                // can be re-read later for anything the fields above do not say
                // (an `igsh` share id, an ad platform's other parameters).
                raw: {
                    ...(request.headers.get('user-agent') ? { userAgent: request.headers.get('user-agent') } : {}),
                    ...(request.headers.get('referer') ? { referer: request.headers.get('referer') } : {}),
                    ...(url.search ? { query: url.search } : {}),
                },
                ...facts,
                ...extra,
            }),
        }).catch(() => undefined));
    };

    /**
     * A follow that goes nowhere — an unknown slug, or a link retired or outside
     * its window. Logged, so a dead URL still in circulation shows up, and never
     * as an open. The person lands on the home page of the host they followed
     * (or, for the path form, on the page it names): whatever the URL was, it
     * was ours, and our front door is a better answer than an error.
     */
    const dead = (outcome: DeadLinkOutcome) => {
        if (caller === 'person') record({ arrivalId: newArrivalId(now), outcome });
        return Response.redirect(pageUrl ?? new URL('/', appOrigin).toString(), 302);
    };

    // 1. The link: the cached copy, refreshed behind the follower when stale.
    const cached = env.cache
        ? await (env.cache.get(slug, 'json') as Promise<CachedLink | null>).catch(() => null)
        : null;
    let link = cached?.link ?? null;
    if (!link) {
        const answer = await fetchLink(env.apiBase, slug);
        // The backend is down or misconfigured: let the app's own page answer
        // this URL, as it did before there was an edge. Better an uncounted
        // arrival than a working link declared ended.
        if (answer === 'unreachable') return opts.fallback();
        if (answer === 'missing') return dead('unknown');
        link = answer;
        if (env.cache) waitUntil(env.cache.put(slug, JSON.stringify({ link, fetchedAt: Date.now() })));
    } else if (env.cache && !cached?.pushed && Date.now() - (cached?.fetchedAt ?? 0) > STALE_AFTER_MS) {
        const cache = env.cache;
        waitUntil((async () => {
            const fresh = await fetchLink(env.apiBase, slug);
            if (typeof fresh !== 'string') await cache.put(slug, JSON.stringify({ link: fresh, fetchedAt: Date.now() }));
        })());
    }

    // A slug that exists but is not this page's link: the path form never
    // sends anyone somewhere other than the page it names.
    if (pageUrl && pageLinkOf(link)?.page !== page) return Response.redirect(pageUrl, 302);

    // 2. A platform drawing its card: tags, and never an arrival.
    if (caller === 'previewer') {
        record({ preview: true, arrivalId: newArrivalId(now) });
        return new Response(previewHtml(link, url.toString()), {
            headers: { 'content-type': 'text/html; charset=utf-8', 'cache-control': 'public, max-age=600' },
        });
    }

    // 3. Outside its window, or retired.
    if (!isEdgeLinkLive(link, now)) return dead('ended');

    // 4. A person. 302 and never 301: a browser caches a permanent redirect, so
    // a repointed link would keep sending them to the old place, uncounted.
    // A page link lands on the host that was followed — the domain the caption
    // showed — whichever of our hosts the link's URL was written with.
    const arrivalId = newArrivalId(now);
    const landing: EdgeLink = pageUrl && link.destination.kind === 'site'
        ? { ...link, destination: { kind: 'route', path: new URL(pageUrl).pathname + new URL(link.destination.url).search } }
        : link;
    const target = redirectTarget(landing, { appOrigin, arrivalId, channel: facts.channel, utm: facts.utm, click: facts.click });
    if (caller === 'person') record({ arrivalId, via: 'edge' });

    return new Response(null, { status: 302, headers: { location: target, 'cache-control': 'no-store' } });
}
